import React from 'react';
import { GRADING_REQUIREMENTS, GRADE_ORDER, GRADE_LABELS } from '../constants/grading';
import { GRADING_ELIGIBILITY_COLORS } from '../constants/colors';

/**
 * GradeProgressionBar — horizontal segmented "career journey" bar.
 *
 * Adapted from RiskReductionBar.js (same absolute-positioned sliding marker +
 * `transition: left 250ms ease-out` technique, same dark-mode segment idiom).
 * Where the RA bar plots an HRN value across risk bands, this bar plots a crew
 * member's accumulated WEEKS across the four grade thresholds of a discipline:
 *
 *   Trainee → Technician → Senior Tech → Supervisor
 *
 * Like the RA bar, the four zones are drawn EQUAL-WIDTH (with linear intra-zone
 * scaling) so the journey reads as four balanced steps even though the week
 * ranges between thresholds differ. A "you are here" marker slides to the
 * member's total weeks. The final Supervisor zone extends past supervisor.minWeeks
 * so the marker has breathing room; totals beyond that clamp to the right edge.
 *
 * API
 * ---
 * Default export: <GradeProgressionBar progress={...} discipline="physical" />
 *   - progress    object from utils/gradingStats.computeGradingProgress(...)
 *                 (page computes once, passes down). REQUIRED.
 *                 progress.discipline is the AUTHORITATIVE source of truth for
 *                 the bar's thresholds — the totals/eligibility carried in
 *                 `progress` were computed against it, so the plotted thresholds
 *                 must match it.
 *   - discipline  optional discipline id, kept for backward-compat only. It is
 *                 used as a FALLBACK when progress.discipline is absent; when
 *                 both are present, progress.discipline wins.
 *   The bar renders the eligibility chip row beneath itself, so consuming the
 *   default export alone gives you bar + chips.
 *
 * Named export: <EligibilitySummary progress={...} /> — the chip row on its own,
 *   in case the page (Task 5) wants to place it elsewhere.
 */


// Eligibility status -> badge colour classes — sourced from the shared palette
// in constants/colors.js as GRADING_ELIGIBILITY_COLORS, and re-exported here
// under the local STATUS_PILL name so existing callers don't break.
export const STATUS_PILL = GRADING_ELIGIBILITY_COLORS;

export const STATUS_LABEL = {
  pass: 'Met',
  borderline: 'Borderline',
  fail: 'Not yet',
};

/**
 * Build the four zone boundaries (cumulative minWeeks) for a discipline.
 * trainee=0, technician/senior/supervisor from requirements.
 * Returns ascending array of 4 thresholds, e.g. [0, 200, 400, 600].
 */
function thresholdsFor(disc) {
  const reqs = GRADING_REQUIREMENTS[disc] || GRADING_REQUIREMENTS.physical;
  return GRADE_ORDER.map((grade) => Number(reqs[grade]?.minWeeks) || 0);
}

/**
 * Map accumulated weeks to a 0–100 percent position on the EQUAL-WIDTH bar.
 *
 * The bar has 3 inter-grade segments (trainee→tech, tech→senior, senior→super),
 * each rendered at equal pixel width. Within a segment, weeks scale linearly.
 * Weeks at/over the supervisor threshold clamp to 100%.
 */
function positionFor(weeks, thresholds) {
  const n = Math.max(0, Number(weeks) || 0);
  const segs = thresholds.length - 1; // 3 segments between 4 thresholds
  if (segs <= 0) return 0;
  if (n >= thresholds[segs]) return 100;

  for (let i = 0; i < segs; i++) {
    const lo = thresholds[i];
    const hi = thresholds[i + 1];
    if (n < hi) {
      const span = hi - lo;
      const intra = span > 0 ? (n - lo) / span : 0;
      const clamped = Math.max(0, Math.min(1, intra));
      return ((i + clamped) / segs) * 100;
    }
  }
  return 100;
}

function formatWeeks(w) {
  return Math.round(Number(w) || 0);
}

/**
 * Compact eligibility chip row. One chip per non-trainee grade. Each chip shows
 * the grade label + a status pill; `reasons` are surfaced via a title tooltip.
 */
export function EligibilitySummary({ progress }) {
  const eligibility = Array.isArray(progress?.eligibility) ? progress.eligibility : [];
  if (eligibility.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="grade-eligibility">
      {eligibility.map((item) => {
        const status = item.status in STATUS_PILL ? item.status : 'fail';
        const reasons = Array.isArray(item.reasons) ? item.reasons : [];
        const title = reasons.length > 0 ? reasons.join('\n') : `${GRADE_LABELS[item.grade] || item.grade}: requirements met`;
        return (
          <span
            key={item.grade}
            title={title}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-white text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 cursor-help"
          >
            <span>{GRADE_LABELS[item.grade] || item.grade}</span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[status]}`}>
              {STATUS_LABEL[status]}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export default function GradeProgressionBar({ progress, discipline }) {
  // progress.discipline is authoritative: the totals/eligibility in `progress`
  // were computed against it, so its thresholds must be the ones plotted. The
  // `discipline` prop is a backward-compat fallback only.
  if (
    process.env.NODE_ENV !== 'production' &&
    discipline &&
    progress?.discipline &&
    discipline !== progress.discipline
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[GradeProgressionBar] discipline prop (%s) differs from progress.discipline (%s); using progress.discipline',
      discipline,
      progress.discipline,
    );
  }

  const disc =
    (progress?.discipline && GRADING_REQUIREMENTS[progress.discipline] && progress.discipline) ||
    (discipline && GRADING_REQUIREMENTS[discipline] && discipline) ||
    'physical';

  const thresholds = thresholdsFor(disc);
  const segs = thresholds.length - 1; // 3
  const totalWeeks = Math.max(0, Number(progress?.totalWeeks) || 0);

  // Re-baseline when a higher grade is already held: the segments below the
  // current grade are shown FADED green ("presumed via grading record, not
  // proven by logged weeks"), and the marker position is offset by the held
  // grade's cumulative minWeeks so logged weeks stack on top of the floor.
  const presumedMinWeeks = Math.max(0, Number(progress?.presumedMinWeeks) || 0);
  const effectiveWeeks =
    progress && typeof progress.effectiveWeeks === 'number'
      ? Math.max(0, progress.effectiveWeeks)
      : totalWeeks;
  const currentGradeLabel = progress?.currentGrade ? GRADE_LABELS[progress.currentGrade] : null;

  const presumedPct = positionFor(presumedMinWeeks, thresholds);
  const markerPct = positionFor(effectiveWeeks, thresholds);
  const markerLabel = currentGradeLabel
    ? `${formatWeeks(totalWeeks)} wk since ${currentGradeLabel}`
    : `${formatWeeks(totalWeeks)} wk`;
  // Anchor the marker the same way the boundary labels are anchored, so it lines
  // up exactly at the endpoints: left-edge at 0%, right-edge at 100%, centered
  // in between. (A center-anchored marker sits half a glyph off the left-edge
  // "Trainee" label at 0 weeks.)
  const markerAlign =
    markerPct <= 0
      ? 'translate-x-0 items-start text-left'
      : markerPct >= 100
      ? '-translate-x-full items-end text-right'
      : '-translate-x-1/2 items-center text-center';

  const { nextGrade, weeksToNext } = progress || {};
  const caption = nextGrade
    ? `${formatWeeks(weeksToNext)} weeks to ${GRADE_LABELS[nextGrade] || nextGrade}`
    : 'Top grade reached';

  const ariaLabel = `Grade progression: ${formatWeeks(totalWeeks)} weeks accumulated. ${caption}.`;

  return (
    <div className="w-full" data-testid="grade-progression-bar">
      {/* "You are here" marker — slides on prop change, same idiom as RA bar */}
      <div className="relative h-5">
        <div
          className={`absolute flex flex-col ${markerAlign} -translate-y-1 pointer-events-none leading-tight`}
          style={{ left: `${markerPct}%`, transition: 'left 250ms ease-out' }}
        >
          <span className="text-xs font-medium whitespace-nowrap text-green-600 dark:text-green-400">
            {markerLabel}
          </span>
          <span className="text-green-600 dark:text-green-400 text-[11px] -mt-0.5" aria-hidden="true">▼</span>
        </div>
      </div>

      {/* The bar — grey track with up to TWO green fills:
            1. FADED green from 0% to presumedPct (credit via current grade —
               not proven by logged weeks, just presumed because the applicant
               already holds this grade).
            2. SOLID green from presumedPct to markerPct (proven by the weeks
               logged in the experience section).
          Plus faint dividers at each grade threshold so the four-step
          structure stays readable through both fills. */}
      <div
        role="img"
        aria-label={ariaLabel}
        className="relative w-full h-2 rounded-sm overflow-hidden border border-gray-200/60 dark:border-gray-700/40 bg-gray-100 dark:bg-gray-700/40"
      >
        {presumedPct > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-green-300/50 dark:bg-green-600/30"
            style={{ width: `${presumedPct}%`, transition: 'width 250ms ease-out' }}
            data-testid="grade-bar-presumed-fill"
          />
        )}
        {markerPct > presumedPct && (
          <div
            className="absolute inset-y-0 bg-green-400 dark:bg-green-500/60"
            style={{
              left: `${presumedPct}%`,
              width: `${markerPct - presumedPct}%`,
              transition: 'left 250ms ease-out, width 250ms ease-out',
            }}
            data-testid="grade-bar-proven-fill"
          />
        )}
        {thresholds.slice(1, -1).map((_, i) => (
          <div
            key={i}
            className="absolute inset-y-0 w-px bg-gray-300/70 dark:bg-gray-500/50"
            style={{ left: `${((i + 1) / segs) * 100}%` }}
          />
        ))}
      </div>

      {/* Boundary labels — positioned at each threshold's TRUE position on the
          bar (0, 1/3, 2/3, 1), each centered under its tick with an up-caret
          pointing at the bar. This makes the sliding marker line up exactly with
          a grade's label when weeks reach that threshold (e.g. 200 wk ↔ the
          Technician caret). Edge labels align inward so they don't clip the
          component edges, while their caret still sits on the tick. (Centered
          long labels can crowd on very narrow widths — acceptable trade-off for
          accurate alignment.) */}
      <div className="relative mt-0.5 h-10 leading-tight">
        {thresholds.map((wk, i) => {
          const pct = segs > 0 ? (i / segs) * 100 : 0;
          const isFirst = i === 0;
          const isLast = i === thresholds.length - 1;
          // The whole block is shifted so its caret edge lands on the tick.
          const blockClass = isFirst
            ? 'translate-x-0 items-start text-left'
            : isLast
            ? '-translate-x-full items-end text-right'
            : '-translate-x-1/2 items-center text-center';
          // Three-way label colouring:
          //   proven   — marker has reached this threshold via logged weeks
          //              (i.e. weeks PAST the presumed floor) → bright green
          //   presumed — covered by the held grade's credit but not by logged
          //              weeks → faded green (matches the faded fill)
          //   pending  — beyond the marker → grey
          const proven = effectiveWeeks >= wk && wk >= presumedMinWeeks;
          const presumed = !proven && presumedMinWeeks >= wk;
          let caretClass; let labelClass; let weekClass;
          if (proven) {
            caretClass = 'text-green-600 dark:text-green-400';
            labelClass = 'font-semibold text-green-600 dark:text-green-400';
            weekClass = 'text-green-600/80 dark:text-green-400/80';
          } else if (presumed) {
            caretClass = 'text-green-500/60 dark:text-green-500/50';
            labelClass = 'font-medium text-green-700/70 dark:text-green-400/60';
            weekClass = 'text-green-600/50 dark:text-green-400/40';
          } else {
            caretClass = 'text-gray-400 dark:text-gray-600';
            labelClass = 'font-medium text-gray-600 dark:text-gray-300';
            weekClass = 'text-gray-400 dark:text-gray-500';
          }
          return (
            <div
              key={GRADE_ORDER[i]}
              className={`absolute top-0 flex flex-col ${blockClass}`}
              style={{ left: `${pct}%` }}
            >
              <span className={`text-[10px] -mb-0.5 ${caretClass}`} aria-hidden="true">▲</span>
              <span className={`text-[10px] whitespace-nowrap ${labelClass}`}>
                {GRADE_LABELS[GRADE_ORDER[i]]}
              </span>
              <span className={`text-[10px] whitespace-nowrap ${weekClass}`}>
                {wk} wk
              </span>
            </div>
          );
        })}
      </div>

    </div>
  );
}
